/* global PLATFORM */
/* eslint-disable default-param-last */
import { isObj } from 'lib/util/index'
import { intersperse } from 'lib/util/array'

/**
 * Formats a string by replacing its wildcards with variable values.
 *
 * @example
 *  format("Hello, {name}, are you feeling {adjective}?", {name:"Gabriel", adjective: "OK"});
 *  result: Hello, Gabriel, are you feeling OK?
 *
 *  format("Hello, {0}, are you feeling {1}?", "Gabriel", "OK");
 *  result: Hello, Gabriel, are you feeling OK?
 *
 * Adaptation of the formatUnicorn function from StackOverflow: https://stackoverflow.com/a/18234317
 *
 * @param {string} string           the string to format
 * @param {Object|string[]} values  the values to replace the wildcards with
 * @returns {string}                the resulting string
 */
const format = (string, ...values) => {
  const isMarkdown = isObj(string)
  let result = isMarkdown ? string.props?.children || string.props?.text : string

  if (values.length) {
    const type = typeof values[0]
    const args = type === 'string' || type === 'number'
      ? Array.prototype.slice.call(values)
      : values[0]

    Object.keys(args).forEach((key) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'gi'), args[key])
    })
  }

  return isMarkdown
    ? {
      ...string,
      props: {
        ...string.props,
        children: result,
        text: result,
      },
    }
    : result
}

/**
 * Formats the display of `value` according to `numberOfDigits`.
 *
 * @param {string|number} value
 * @param {number} [numberOfDigits=1]
 * @returns {string|number}
 */
export const formatDigits = (value, numberOfDigits) => {
  if (!numberOfDigits || (numberOfDigits && numberOfDigits <= 0)) {
    return value
  }

  // 10^2 -> 100 -> -1 -> 99
  // 10^3 -> 1000 -> -1 -> 999
  const maxValue = (10 ** numberOfDigits) - 1

  return value > maxValue
    ? `${maxValue}+`
    : value
}

/**
 * Add `string` to `file`. Works for both filenames with and without extensions.
 *
 * @param {String} string           the string to append or prepend
 * @param {String} file             the file name to append or prepend to
 * @param {String} [separator='-']  the separator between `file` and `string`
 * @param {Boolean} [append=true]   whether to append or prepend
 * @returns {string}
 */
export const addToFilename = (string, file, separator = '-', append = true) => {
  const i = file.lastIndexOf('.')
  return i === -1
    ? (append && file + separator + string) || string + separator + file
    : (append && file.substring(0, i) + separator + string + file.substring(i))
      || string + separator + file
}

/**
 * Creates a sub label based on `parts`.
 *
 * @param {React.ReactNode} Template    component to use
 * @param {unknown[]} parts             parts to build the sub label from
 * @param {string} separator            separator character in between each part
 * @param {Object} props                optional props for each {@link Text} component
 * @returns {React.ReactNode[]}
 */
export const getSubLabel = (Template, parts, separator = '•', props = {}) => {
  const fontSize = PLATFORM === 'web' ? 12 : 14
  const colorStrength = PLATFORM === 'web' ? 400 : 500

  return intersperse(
    (parts || []).filter(x => !!x), separator,
  ).map(
    (x, i, arr) => (
      <Template
        key={i}
        value={x}
        color={`gray.${colorStrength}`}
        maxLines={i === arr.length - 1 ? 1 : 0}
        style={{ whiteSpace: i === arr.length - 1 ? 'pre-line' : 'nowrap' }}
        variant={i === arr.length - 1 ? `${fontSize}/medium` : `${fontSize}/regular`}
        {...props}
      />
    ),
  )
}

/**
 * Inserts a zero-width space between each character of a string
 * to allow for character-level line breaking.
 *
 * @param {string} string
 * @returns {string}
 */
export const makeBreakable = string => (!string?.length ? string : string.split('').join('\u200B'))

/**
 * Capitalizes `string`.
 *
 * Platform-agnostic variant. Please also mind `capitalizeFirstLetter` in
 * `client/platform/web/src/lib/react/hoc/children.jsx`.
 *
 * @param {String} string the string to capitalize
 * @returns {string}
 */
export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1)

export default format
